import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { VwPluralPipe } from '../../../@commons/pipes/vw-plural.pipe';
import { ListRowComponent } from '../../../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../../../@commons/ui-components/list-row/list-section.component';

export interface ClimateOption {
  climateType: string;
  cultivationCount: number;
}

export interface ClimateGroup {
  key: string;
  emoji: string;
  climates: ClimateOption[];
}

const FAMILY_LABELS: Record<string, { emoji: string; label: string }> = {
  A: { emoji: '🌴', label: 'Tropical' },
  B: { emoji: '☀️', label: 'Aride' },
  C: { emoji: '🌤', label: 'Tempéré' },
  D: { emoji: '❄️', label: 'Continental' },
  E: { emoji: '🏔', label: 'Polaire' },
};
/** Familles Köppen nommées dans les six langues (`calendar.climate.families.<lettre>`). */
const KNOWN_FAMILIES = ['A', 'B', 'C', 'D', 'E'];

@Component({
  selector: 'vw-climate-picker-toast',
  standalone: true,
  imports: [CommonModule, SharedModule, TranslatePipe, ListSectionComponent, ListRowComponent],
  providers: [VwPluralPipe],
  templateUrl: './climate-picker-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './climate-picker-toast.component.scss'
})
export class ClimatePickerToastComponent extends BaseToastComponent implements OnChanges {
  @Input() availableClimates: ClimateOption[] = [];
  @Input() selectedClimateType: string | null = null;
  @Input() myClimateType: string | null = null;
  @Output() climateSelected = new EventEmitter<string | null>();
  @Output() infoClick = new EventEmitter<void>();

  groups: ClimateGroup[] = [];

  private readonly translate = inject(TranslateService);
  private readonly plural = inject(VwPluralPipe);

  constructor() {
    super();
  }

  ngOnChanges() {
    this.buildGroups();
  }

  buildGroups() {
    const map = new Map<string, ClimateOption[]>();
    for (const c of this.availableClimates) {
      const family = c.climateType[0].toUpperCase();
      const climates = map.get(family) ?? [];
      climates.push(c);
      map.set(family, climates);
    }
    this.groups = Array.from(map.entries()).map(([key, climates]) => ({
      key,
      emoji: FAMILY_LABELS[key]?.emoji ?? '🌍',
      climates,
    }));
  }

  selectClimate(type: string | null) {
    this.climateSelected.emit(type);
    this.onClose();
  }

  getFamilyLabel(key: string): string {
    return FAMILY_LABELS[key]?.label ?? key;
  }

  /** Titre de la section : clé traduite ; une famille inconnue garde sa lettre. */
  familyKey(key: string): string {
    return KNOWN_FAMILIES.includes(key) ? `calendar.climate.families.${key}` : key;
  }

  /** Ton climat est dans la liste : sa ligne porte la mention, pas de ligne en plus. */
  get myClimateListed(): boolean {
    return !!this.myClimateType && this.availableClimates.some((c) => c.climateType === this.myClimateType);
  }

  isMine(type: string): boolean {
    return type === this.myClimateType;
  }

  /** Coché : le climat choisi, ou le sien quand aucun n'est choisi. */
  isChecked(type: string): boolean {
    return this.selectedClimateType === type || (this.selectedClimateType === null && this.isMine(type));
  }

  /** « 128 cultures », précédé de « Ton climat » sur le sien. */
  climateSubtitle(climate: ClimateOption): string {
    const count = this.plural.transform('calendar.climate.picker.count', climate.cultivationCount);
    return this.isMine(climate.climateType) ? `${this.translate.instant('calendar.climate.myClimate')}, ${count}` : count;
  }
}
